import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { forceQualifyAPI, jobTitleFiltersAPI, settingsAPI } from '../lib/api';
import type { JobTitleFilter, JobTitleSynonym } from '../types/leads';

export function useJobTitleFilters(organizationId: string | undefined) {
  return useQuery<JobTitleFilter[]>({
    queryKey: ['jobTitleFilters', organizationId],
    queryFn: async () => {
      const { data } = await jobTitleFiltersAPI.getAll(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateJobTitleFilter() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (payload: {
      title: string;
      country?: string;
      industry?: string;
      scoreBonus?: number;
    }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await jobTitleFiltersAPI.create(orgId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobTitleFilters', currentOrganization?.id] });
    },
  });
}

export function useToggleJobTitleFilter() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await jobTitleFiltersAPI.update(orgId, id, { isActive });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobTitleFilters', currentOrganization?.id] });
    },
  });
}

export function useDeleteJobTitleFilter() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await jobTitleFiltersAPI.delete(orgId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobTitleFilters', currentOrganization?.id] });
    },
  });
}

export function useJobTitleSynonyms(organizationId: string | undefined) {
  return useQuery<JobTitleSynonym[]>({
    queryKey: ['jobTitleSynonyms', organizationId],
    queryFn: async () => {
      const { data } = await jobTitleFiltersAPI.getSynonyms(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateJobTitleSynonym() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (payload: { synonym: string; canonicalTitle: string }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await jobTitleFiltersAPI.createSynonym(orgId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobTitleSynonyms', currentOrganization?.id] });
    },
  });
}

export function useDeleteJobTitleSynonym() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await jobTitleFiltersAPI.deleteSynonym(orgId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobTitleSynonyms', currentOrganization?.id] });
    },
  });
}

export function useForceQualifyLead() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, reason }: { leadId: string; reason: string }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await forceQualifyAPI.forceQualify(leadId, orgId, reason);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
    },
  });
}

export function useJobTitleFilterEnabled(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['orgSettings', organizationId],
    queryFn: async () => {
      const { data } = await settingsAPI.getOrgSettings(organizationId!);
      const overrides = (data.settingsOverrides as Record<string, unknown>) || {};
      return overrides.jobTitleFilterEnabled === true;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useToggleJobTitleFilterEnabled() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await settingsAPI.updateOrgSettings(orgId, {
        jobTitleFilterEnabled: enabled,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgSettings', currentOrganization?.id] });
    },
  });
}
